import { useQuery, useMutation, useQueryClient } from "react-query";
import api from "../services/api";

// Fetch trainers list from /trainer_assign/all-trainers
export const fetchTrainers = async () => {
  try {
    const res = await api.get("/trainer_assign/all-trainers");
    if (res.data && Array.isArray(res.data) && res.data.length > 0) {
      return res.data;
    }
  } catch (err) {
    console.log("Error fetching /trainer_assign/all-trainers, trying fallback", err);
  }
  return [];
};

// Hook for fetching all trainers list with React Query cache
export const useTrainers = (options = {}) => {
  return useQuery(["all-trainers-assigned-list"], fetchTrainers, {
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    ...options,
  });
};

// Hook for fetching FDP & Tech Camp limits
export const fetchLimitsData = async () => {
  try {
    const res = await api.get("/event/fdp_tech_camp_limits");
    return res.data;
  } catch (err) {
    console.log("Error fetching limits data", err);
    return null;
  }
};

export const useLimitsData = (options = {}) => {
  return useQuery(["fdp-techcamp-limits"], fetchLimitsData, {
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    ...options,
  });
};

// Hook for fetching assigned trainer for the institute from /trainer_assign/my-trainers
export const fetchAssignedTrainer = async () => {
  try {
    const res = await api.get("/trainer_assign/my-trainers");
    return res.data;
  } catch (err) {
    if (err?.response?.status === 404) {
      return { assigned_trainers: [] };
    }
    console.log("Error fetching /trainer_assign/my-trainers", err);
    return null;
  }
};

export const useAssignedTrainer = (options = {}) => {
  return useQuery(["institute-assigned-trainer"], fetchAssignedTrainer, {
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    ...options,
  });
};

// Hook for assigning trainer to institute mutation
export const useAssignTrainers = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (payload) => {
      const res = await api.post("/trainer_assign/assign", payload);
      return res.data;
    },
    {
      onSuccess: () => {
        // queryClient.invalidateQueries(["all-trainers-assigned-list"]);
        // queryClient.invalidateQueries(["institute-assigned-trainer"]);
        // queryClient.invalidateQueries(["all-assignments"]);
      },
    }
  );
};

// Hook for requesting trainer change mutation (coordinator reviews manually)
export const useRequestTrainerChange = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async (payload) => {
      const res = await api.post("/trainer_assign/change-request", payload);
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["my-trainer-change-requests"]);
        queryClient.invalidateQueries(["institute-assigned-trainer"]);
      },
    }
  );
};

// Fetch trainer change requests for current institute from /trainer_assign/my-requests
export const fetchMyRequests = async ({ status, page = 1, limit = 10 } = {}) => {
  try {
    const params = { page, limit };
    if (status) params.status = status;

    const res = await api.get("/trainer_assign/my-requests", { params });
    return res.data;
  } catch (err) {
    if (err?.response?.status === 404) {
      return { page: 1, limit: 10, total: 0, total_pages: 0, data: [] };
    }
    console.log("Error fetching my trainer change requests", err);
    return null;
  }
};

// Hook for fetching institute's own trainer change requests
export const useMyTrainerRequests = (
  { status, page = 1, limit = 10 } = {},
  options = {}
) => {
  return useQuery(
    ["my-trainer-change-requests", { status, page, limit }],
    () => fetchMyRequests({ status, page, limit }),
    {
      staleTime: 1 * 60 * 1000,
      refetchOnWindowFocus: true,
      ...options,
    }
  );
};

// Fetch trainer change requests for coordinator from /trainer_assign/cordinator/get-requests
export const fetchCoordinatorRequests = async ({ status, search, page = 1, limit = 10 } = {}) => {
  try {
    const params = { page, limit };
    if (status) params.status = status;
    const trimmedSearch = typeof search === "string" ? search.trim() : "";
    if (trimmedSearch) params.search = trimmedSearch;

    const res = await api.get("/trainer_assign/cordinator/get-requests", { params });
    return res.data;
  } catch (err) {
    if (err?.response?.status === 404) {
      return { page: 1, limit, total: 0, total_pages: 0, data: [] };
    }
    console.log("Error fetching coordinator requests", err);
    return { page: 1, limit, total: 0, total_pages: 0, data: [] };
  }
};

// Hook for fetching coordinator trainer change requests
export const useCoordinatorRequests = (
  { status, search, page = 1, limit = 10 } = {},
  options = {}
) => {
  const normalizedSearch = typeof search === "string" ? search.trim() : "";
  return useQuery(
    ["coordinator-trainer-requests", { status: status || null, search: normalizedSearch, page, limit }],
    () => fetchCoordinatorRequests({ status, search: normalizedSearch, page, limit }),
    {
      staleTime: 30 * 1000,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      ...options,
    }
  );
};

// Hook for resolving trainer change request (approve / reject)
export const useResolveChangeRequest = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ requestId, action, reason }) => {
      const res = await api.put(`/trainer_assign/cordinator/change-request/${requestId}`, {
        action,
        reason: reason || undefined,
      });
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["coordinator-trainer-requests"]);
      },
    }
  );
};

// Fetch filter options (states, cities) for trainer's assigned institutes
export const fetchMyInstituteFilterOptions = async ({ state_id } = {}) => {
  try {
    const params = {};
    if (state_id !== undefined && state_id !== null && state_id !== "") {
      params.state_id = state_id;
    }
    try {
      const res = await api.get("/trainer_assign/my-institute-assignments/filter-options", { params });
      return res.data || { states: [], cities: [] };
    } catch (err) {
      if (err?.response?.status === 404) {
        const fallbackRes = await api.get("/my-institute-assignments/filter-options", { params });
        return fallbackRes.data || { states: [], cities: [] };
      }
      throw err;
    }
  } catch (err) {
    console.log("Error fetching my institute filter options", err);
    return { states: [], cities: [] };
  }
};

// Hook for fetching filter options
export const useMyInstituteFilterOptions = ({ state_id } = {}, options = {}) => {
  return useQuery(
    ["my-institute-filter-options", { state_id: state_id || null }],
    () => fetchMyInstituteFilterOptions({ state_id }),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      ...options,
    }
  );
};

// Fetch trainer's assigned institutes from /trainer_assign/my-institute-assignments
export const fetchMyInstituteAssignments = async ({
  search,
  page = 1,
  limit = 10,
  state_id,
  city_name,
  expiry_date,
  expire_date,
  fdp_total,
  fdp_used,
  fdp_available,
  tech_camp_total,
  tech_camp_used,
  tech_camp_available,
} = {}) => {
  try {
    const params = { page, limit };
    const trimmedSearch = typeof search === "string" ? search.trim() : "";
    if (trimmedSearch) params.search = trimmedSearch;
    if (state_id !== undefined && state_id !== null && state_id !== "") params.state_id = state_id;
    if (city_name && String(city_name).trim()) params.city_name = String(city_name).trim();

    const effExpiry = expiry_date || expire_date;
    if (effExpiry && String(effExpiry).trim()) params.expiry_date = String(effExpiry).trim();

    if (fdp_total !== undefined && fdp_total !== null && fdp_total !== "") params.fdp_total = Number(fdp_total);
    if (fdp_used !== undefined && fdp_used !== null && fdp_used !== "") params.fdp_used = Number(fdp_used);
    if (fdp_available !== undefined && fdp_available !== null && fdp_available !== "") params.fdp_available = Number(fdp_available);

    if (tech_camp_total !== undefined && tech_camp_total !== null && tech_camp_total !== "") params.tech_camp_total = Number(tech_camp_total);
    if (tech_camp_used !== undefined && tech_camp_used !== null && tech_camp_used !== "") params.tech_camp_used = Number(tech_camp_used);
    if (tech_camp_available !== undefined && tech_camp_available !== null && tech_camp_available !== "") params.tech_camp_available = Number(tech_camp_available);

    try {
      const res = await api.get("/trainer_assign/my-institute-assignments", { params });
      return res.data;
    } catch (err) {
      if (err?.response?.status === 404) {
        const fallbackRes = await api.get("/my-institute-assignments", { params });
        return fallbackRes.data;
      }
      throw err;
    }
  } catch (err) {
    if (err?.response?.status === 404) {
      return { page: 1, limit, total: 0, total_pages: 0, data: [] };
    }
    console.log("Error fetching my institute assignments", err);
    return { page: 1, limit, total: 0, total_pages: 0, data: [] };
  }
};

// Hook for fetching trainer's assigned institutes
export const useMyInstituteAssignments = (
  {
    search,
    page = 1,
    limit = 10,
    state_id,
    city_name,
    expiry_date,
    expire_date,
    fdp_total,
    fdp_used,
    fdp_available,
    tech_camp_total,
    tech_camp_used,
    tech_camp_available,
  } = {},
  options = {}
) => {
  const normalizedSearch = typeof search === "string" ? search.trim() : "";
  const effExpiry = expiry_date || expire_date || "";

  return useQuery(
    [
      "my-institute-assignments",
      {
        search: normalizedSearch,
        page,
        limit,
        state_id: state_id || "",
        city_name: city_name || "",
        expiry_date: effExpiry,
        fdp_total: fdp_total ?? "",
        fdp_used: fdp_used ?? "",
        fdp_available: fdp_available ?? "",
        tech_camp_total: tech_camp_total ?? "",
        tech_camp_used: tech_camp_used ?? "",
        tech_camp_available: tech_camp_available ?? "",
      },
    ],
    () =>
      fetchMyInstituteAssignments({
        search: normalizedSearch,
        page,
        limit,
        state_id,
        city_name,
        expiry_date: effExpiry,
        fdp_total,
        fdp_used,
        fdp_available,
        tech_camp_total,
        tech_camp_used,
        tech_camp_available,
      }),
    {
      staleTime: 30 * 1000,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      ...options,
    }
  );
};

// Fetch assigned institutes for trainer dropdown from /trainer_assign/trainer-institutes
export const fetchTrainerInstitutes = async () => {
  try {
    const res = await api.get("/trainer_assign/trainer-institutes");
    return res.data || [];
  } catch (err) {
    if (err?.response?.status === 404) {
      return [];
    }
    console.log("Error fetching trainer institutes", err);
    return [];
  }
};

// Hook for fetching institutes dropdown for trainer
export const useTrainerInstitutes = (options = {}) => {
  return useQuery(["trainer-assigned-institutes-dropdown"], fetchTrainerInstitutes, {
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    ...options,
  });
};

// Fetch assigned domains for trainer dropdown from /trainer_assign/trainer-domains or /trainer-domains
export const fetchTrainerDomains = async (params = {}) => {
  try {
    const res = await api.get("/trainer_assign/trainer-domains", { params });
    if (res?.data && Array.isArray(res.data)) {
      return res.data;
    }
  } catch (err) {
     
    console.log("Error fetching trainer domains", err);
    
  }
  return [];
};

// Hook for fetching domains dropdown for trainer
export const useTrainerDomains = (paramsOrOptions = {}, options = {}) => {
  const isOptions =
    paramsOrOptions &&
    (paramsOrOptions.enabled !== undefined ||
      paramsOrOptions.staleTime !== undefined ||
      paramsOrOptions.select !== undefined ||
      paramsOrOptions.onSuccess !== undefined);

  const params = isOptions ? {} : paramsOrOptions;
  const queryOptions = isOptions ? paramsOrOptions : options;

  return useQuery(
    ["trainer-assigned-domains-dropdown", params],
    () => fetchTrainerDomains(params),
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      ...queryOptions,
    }
  );
};

// Fetch calendar availability for trainer booking
export const fetchTrainerCalendarAvailability = async ({ institute_id, domain_id, event_type }) => {
  if (!institute_id || !domain_id || !event_type) return null;
  try {
    const res = await api.post("/trainer_assign/calendar-availability", {
      institute_id: Number(institute_id),
      domain_id: Number(domain_id),
      event_type,
    });
    return res.data;
  } catch (err) {
    console.log("Error fetching trainer calendar availability", err);
    throw err;
  }
};

// Hook for fetching calendar availability for a trainer slot booking
export const useTrainerCalendarAvailability = (
  { institute_id, domain_id, event_type } = {},
  options = {}
) => {
  return useQuery(
    ["trainer-calendar-availability", { institute_id, domain_id, event_type }],
    () => fetchTrainerCalendarAvailability({ institute_id, domain_id, event_type }),
    {
      enabled: Boolean(institute_id && domain_id && event_type),
      staleTime: 30 * 1000,
      refetchOnWindowFocus: false,
      ...options,
    }
  );
};
// Hook for booking a trainer slot mutation
export const useBookTrainerSlot = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (formData) => {
      const res = await api.post("/trainer_assign/book-slot", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["trainer-calendar-availability"]);
        queryClient.invalidateQueries(["my-institute-assignments"]);
        queryClient.invalidateQueries(["trainer-assigned-institutes-dropdown"]);
      },
    }
  );
};
